import { React, _ } from '../../../lib'
import { Alerts } from '../../component-main'
import { reactFlyout } from '../../component-react'
import { NewSystemFlyoutInstance } from '../../flyouts'
import { FormButton, FormButtonSet } from '../buttons'
import { Combobox } from '../combobox'
import { FileUploadItem } from '../file-upload'
import { CJSX } from '../meta-types'
import { FormRow } from '../wrappers'
import { getTagComboboxOptions } from './tags'
import { ReducerState } from './types'

export const bulkUploadNotes = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
	filesAll: FileUploadItem[],
	onResolve: () => void,
) => {
	// Remove duplicates - don't allow bulk-uploading something that is already
	// in the current component instance
	const existingAttachmentChecksums = _.chain(rs.props.notes)
		.filter(x => !x.Deleted && !x.Archived)
		.map(x => x.Attachment?.Checksum)
		.compact()
		.uniq()
		.value()
	const files = filesAll.filter(x => !existingAttachmentChecksums.includes(x.Checksum))

	// If there are no files left, just let the user know and resolve
	if (files.length == 0) {
		Alerts.Alert({ msg: 'No new files to upload' })
		onResolve()
		return
	}

	// Render the flyout asking the user to confirm and pick tags
	reactFlyout(
		'Are you sure?',
		[360, 200],
		<BulkUploadNotesConfirm rs={rs} files={files} onResolve={onResolve} />,
		{
			closeCallback: onResolve,
		},
	)
}

const BulkUploadNotesConfirm = <T extends Record<string, unknown>>(props: {
	rs: ReducerState<T>
	files: FileUploadItem[]
	onResolve: () => void
	flyout?: NewSystemFlyoutInstance
}) => {
	const rs = props.rs

	// Manage selected tags - default to current filter selection
	const [tags, setTags] = React.useState<string[]>(rs.state.filteredTags ?? [])

	// Form state
	const [isLoading, setIsLoading] = React.useState(false)
	const [msg, setMsg] = React.useState('')
	const [isDone, setIsDone] = React.useState(false)

	// Get the confirmation prompt text - can be user defined
	const plural = props.files.length > 1 ? 'files' : 'file'
	const confirmationPrompt =
		rs.props.adding?.bulkUpload?.confirmationPrompt?.(props.files) ??
		`Are you sure you want to upload ${props.files.length} ${plural}?`

	// Render
	return (
		<>
			{confirmationPrompt}
			<CJSX cond={Boolean(rs.props.tagging)}>
				<FormRow lbl="Tags">
					<Combobox
						value={tags}
						onUpdate={setTags}
						multiple={true}
						options={getTagComboboxOptions(rs.props.tagging?.options)}
					/>
				</FormRow>
			</CJSX>
			<FormButtonSet loading={isLoading} msg={msg} tick={isDone}>
				<FormButton
					lbl="OK"
					type="submit"
					onClick={() => {
						setIsLoading(true)
						setMsg('')
						rs.props.adding?.bulkUpload?.onUpload(
							{
								files: props.files,
								tags: tags,
							},
							() => {
								setMsg('Done')
								setIsDone(true)
								setIsLoading(false)
								props.onResolve()
								props.flyout?.Close()
							},
						)
					}}
				/>
				<FormButton
					lbl="Cancel"
					onClick={() => {
						props.flyout?.Close()
						props.onResolve()
					}}
				/>
			</FormButtonSet>
		</>
	)
}
